// SPDX-License-Identifier: AGPL-3.0-or-later

import {makeAutoObservable} from 'mobx';

export interface ReminderEntry {
	id: string;
	channel_id: string;
	message_id: string;
	remind_at: string;
}

class Reminders {
	private remindAtByMessageId: Record<string, number> = {};
	private fetchGeneration = 0;

	constructor() {
		makeAutoObservable(
			this,
			{
				getPendingRemindAt: false,
				hasPendingReminder: false,
				snapshotFetchGeneration: false,
			},
			{autoBind: true},
		);
	}

	snapshotFetchGeneration(): number {
		return this.fetchGeneration;
	}

	handleGatewayReady(): void {
		this.fetchGeneration += 1;
		this.remindAtByMessageId = {};
	}

	fetchSuccess(generation: number, entries: ReadonlyArray<ReminderEntry>): void {
		if (generation !== this.fetchGeneration) {
			return;
		}
		const next: Record<string, number> = {};
		for (const entry of entries) {
			const remindAtMs = new Date(entry.remind_at).getTime();
			if (!Number.isFinite(remindAtMs)) {
				continue;
			}
			next[String(entry.message_id)] = remindAtMs;
		}
		this.remindAtByMessageId = next;
	}

	fetchError(_generation: number): void {
		return;
	}

	add(messageId: string, remindAt: Date): void {
		this.fetchGeneration += 1;
		this.remindAtByMessageId = {
			...this.remindAtByMessageId,
			[String(messageId)]: remindAt.getTime(),
		};
	}

	remove(messageId: string): void {
		const key = String(messageId);
		if (!(key in this.remindAtByMessageId)) {
			return;
		}
		this.fetchGeneration += 1;
		const {[key]: _removed, ...rest} = this.remindAtByMessageId;
		this.remindAtByMessageId = rest;
	}

	getPendingRemindAt(messageId: string): number | undefined {
		return this.remindAtByMessageId[String(messageId)];
	}

	hasPendingReminder(messageId: string): boolean {
		return this.getPendingRemindAt(messageId) != null;
	}
}

export default new Reminders();
